// Copyright (c) 2020-2026 Life and Soft

import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

import { GET_ERRORS, SET_CURRENT_USER, USER_LOADING } from "./types";
import setAuthToken from "../utils/setAuthToken";
import Server from "../utils/axios/Server";
import { initConfiguration } from "./configurationsActions";

// Register User
export const registerUser = (userData, navigate) => (dispatch) => {
  Server.post("/users/register", userData)
    .then(() => {
      navigate("/login");
      toast.success("Email sent, please check your inbox to confirm");
    }) // redirect to login on successful register
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Resend confirmation email
export const resendConfirmationUser = (userData, navigate) => (dispatch) => {
  Server.post("/users/resend", userData)
    .then(() => {
      navigate("/login");
      toast.success("Email sent, please check your inbox to confirm");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Login - get user token
export const loginUser = (data) => (dispatch) => {
  // Save to localStorage
  // Set token to localStorage
  const { token, refreshToken } = data;

  localStorage.setItem("jwtToken", token);
  localStorage.setItem("refreshToken", refreshToken);
  // Set token to Auth header
  setAuthToken(token);

  // Get configuration
  Server.get("/configuration")
    .then((res) => {
      const configuration = res.data;
      dispatch(initConfiguration(configuration));

      // Decode token to get user data
      const decoded = jwtDecode(token);

      // Set current user
      dispatch(setCurrentUser(decoded));
      dispatch({ type: GET_ERRORS, payload: "" });
    })
    .catch(() => console.log("Unable to load configuration"));
};

// Forgot password - Send an email for reset password
export const forgotPasswordUser = (userData, auth, navigate) => (dispatch) => {
  Server.post("/users/forgotPassword", userData)
    .then(() => {
      navigate(auth.isAuthenticated ? "/" : "/login");
      toast.success(
        "If your email address exists, you will receive a password recovery link at your email address in a few minutes.",
      );
    })
    .catch((err) => {
      if (err.response.data.isNotEmail) {
        dispatch({
          type: GET_ERRORS,
          payload: err.response.data,
        });
      } else {
        toast.success(
          "If your email address exists, you will receive a password recovery link at your email address in a few minutes.",
        );
        navigate(auth.isAuthenticated ? "/" : "/login");
      }
    });
};

// Reset password - update password
export const resetPasswordUser = (userData, navigate, id) => (dispatch) => {
  Server.put(`/users/resetPassword/${id}`, userData)
    .then(() => {
      navigate("/login");
      toast.success("Your password has been changed successfully.");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Delete Profile account by the account owner
export const deleteUser = (userData, navigate) => (dispatch) => {
  Server.post("/users/deleteProfile", userData)
    .then(() => {
      // Remove token from local storage
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("refreshToken");
      // Remove auth header for future requests
      setAuthToken(false, false);
      // Set current user to empty object {} which will set isAuthenticated to false
      dispatch(setCurrentUser({}));
      navigate("/login");
      toast.success("You have deleted your account.");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Delete Profile account by an admin
export const deleteUserByAdmin = (userData, navigate) => (dispatch) => {
  Server.post("/users/deleteProfile", userData)
    .then(() => {
      if (navigate) navigate("/administration/application");
      toast.success("Account deleted !");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Reset password - update password
export const changePasswordUser =
  (userData, navigate, id, user) => (dispatch) => {
    Server.put(`/users/changePassword/${id}`, userData)
      .then((res) => {
        if (user.role === "Admin" && user.accountStatus === "validated") {
          navigate("/administration/application");
          toast.success("The password has been changed successfully.");
        } else {
          const { token, refreshToken } = res.data;

          localStorage.setItem("jwtToken", token);
          localStorage.setItem("refreshToken", refreshToken);

          // Set token to Auth header
          setAuthToken(token);

          // Decode token to get user data
          const decoded = jwtDecode(token);

          // Set current user
          dispatch(setCurrentUser(decoded));
          dispatch({ type: GET_ERRORS, payload: "" });
          toast.success("Your password has been changed successfully.");
        }
      })
      .catch((err) =>
        dispatch({
          type: GET_ERRORS,
          payload: err.response.data,
        }),
      );
  };

// Update fullname, username and receiveNewsLetterEmails
export const updateProfileUser = (userData, id) => (dispatch) => {
  Server.put(`/users/updateProfile/${id}`, userData)
    .then((res) => {
      toast.success(res.data);
      setTimeout(() => {
        window.location.reload(false);
      }, 700);
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Lock user account
export const lockUser = (userData, id, fullname) => (dispatch) => {
  Server.put(`/users/lock/${id}`, userData)
    .then(() => {
      if (userData.lock === "locked") {
        toast.success("You locked the account of " + fullname);
      } else {
        toast.success("You unlocked the account of " + fullname);
      }
    })
    .catch((err) => console.log(err));
};

// Add User
export const addUser = (userData, navigate) => (dispatch) => {
  Server.post("/users/addUserLocal", userData)
    .then(() => {
      navigate("/administration/application");
      toast.success("User added successfully");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Updates mail preferences
export const mailNotifications = (userData, id) => (dispatch) => {
  Server.put(`/users/mails/${id}`, userData)
    .then(() => {
      toast.success("Mail notification updated");
    })
    .catch((err) =>
      dispatch({
        type: GET_ERRORS,
        payload: err.response.data,
      }),
    );
};

// Set logged in user
export const setCurrentUser = (decoded) => {
  return {
    type: SET_CURRENT_USER,
    payload: decoded,
  };
};

// User loading
export const setUserLoading = () => {
  return {
    type: USER_LOADING,
  };
};

// Log user out
export const logoutUser = (tokenId) => (dispatch) => {
  const token = { token: tokenId };

  // update property
  Server.put("/users/logout", token);
  // Remove token from local storage
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("refreshToken");
  // Remove auth header for future requests
  setAuthToken(false);
  dispatch(initConfiguration({}));
  // Set current user to empty object {} which will set isAuthenticated to false
  dispatch(setCurrentUser({}));
};
